import type {
  ColumnSort,
  OnChangeFn,
  SortingState,
} from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';

import { Icon } from '~/components';

import { useArtistTracksQuery } from '../../hooks/useArtistTracksQuery';
import { useRosterUrlParams } from '../../hooks/useRosterUrlParams';
import type { ArtistDetails } from '../../lib/types';
import { DataError } from '../DataError/DataError';
import { TrackTable } from './TrackTable';

interface TrackListProps {
  uaid: number;
  artistData?: ArtistDetails;
  columnSort?: ColumnSort;
  setSortingState?: OnChangeFn<SortingState> | undefined;
}

export function TrackList({
  uaid,
  artistData,
  columnSort,
  setSortingState,
}: TrackListProps) {
  const { pageNumber } = useRosterUrlParams();
  const { t } = useTranslation('roster');

  const {
    data: tracksData,
    error,
    isLoading: isLoadingTracks,
    isFetching: isFetchingTracks,
  } = useArtistTracksQuery({ uaid, columnSort, pageNumber });

  if (!artistData) {
    return null;
  }

  if (error) {
    return (
      <DataError
        title={t('errorData.title')}
        subtitle={t('errorData.sub')}
        icon={
          <Icon
            type="waveform"
            size="4x"
            variant="light"
            aria-hidden
            className="text-default"
          />
        }
      />
    );
  }

  const trackTableMeta = {
    artistName: artistData.name,
    filtersCount: 0,
    filtersApplied: false,
    partyId: artistData.partyId,
    uaid,
  };

  return (
    <div className="relative w-full">
      <TrackTable
        data={tracksData?.items ?? []}
        meta={trackTableMeta}
        artistData={artistData}
        isLoadingTracks={isLoadingTracks}
        isFetchingTracks={isFetchingTracks}
        columnSort={columnSort}
        onSortingChange={setSortingState}
      />
    </div>
  );
}

TrackList.displayName = 'TrackList';
